import {
  AfterContentInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { CreateEvaluationRequest } from '../../../models/requests/create-evaluation-request';
import { UpdateEvaluationRequest } from '../../../models/requests/update-evaluation-request';
import * as discussionActions from '../../../store/actions/discussion.actions';
import * as potentialCauseActions from '../../../store/actions/potential-cause.actions';
import * as correctiveActionActions from '../../../store/actions/corrective-action.actions';
import { combineLatest, Observable, Subject, take, takeUntil } from 'rxjs';
import { selectAllDiscussions } from '../../../store/selectors/discussions.selector';
import { selectPotentialCause } from '../../../store/selectors/potential-cause.selector';
import { selectCorrectiveAction } from '../../../store/selectors/corrective-action.selector';
import { PotentialCause } from '../../../models/potential-cause.model';
import { CorrectiveAction } from '../../../models/corrective-action.model';
import { Message } from '../../../models/comment.model';
import { AuthService } from '../../../core/services/auth.service';
import { EvaluationService } from '../../../services/evaluation.service';
import { AlarmService } from '../../../services/alarm.service';
import { AddCommentRequest } from '../../../models/requests/add-comment-request';
import { Actions, ofType } from '@ngrx/effects';
import { Alarm } from '../../../models/alarm.model';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { Evaluation } from '../../../models/evaluation.model';
import { threadId } from 'worker_threads';
import {
  createRuleEvaluation,
  updateRuleEvaluation,
  updateRuleEvaluationVigie,
  loadRule,
} from '../../../store/actions/rules.actions';
import { GLOBAL_CONFIRMATION_DELETE } from '../../../shared/constants/global-key.constans';
import { Stage } from '../../../models/rule.model';
import { AlarmStatus } from '../../../../enum/status.enum';
import { RoleService } from '@ind4.0/angular-common-lib';

@Component({
  selector: 'app-evaluation-form',
  templateUrl: './evaluation-form.component.html',
  styleUrl: './evaluation-form.component.scss',
  standalone: false,
})
export class EvaluationFormComponent
  implements OnInit, OnChanges, OnDestroy, AfterContentInit
{
  @Input() selectedAlarm?: Alarm;
  @Input() evaluation?: Evaluation;
  @ViewChildren('commentElement') commentElements!: QueryList<ElementRef>;
  readonly CONFIRM_DELETE = GLOBAL_CONFIRMATION_DELETE;

  evaluationForm!: FormGroup;
  Stage = Stage;
  isSendingToVigie = false;
  isUpdatingVigie = false;

  severityOptions: SelectItem[] = [
    { label: 'Haute', value: 0 },
    { label: 'Moyenne', value: 1 },
    { label: 'Basse', value: 2 },
  ];
  priorityOptions = [...this.severityOptions];
  potentialCauses: PotentialCause[] = [];
  correctiveActions: CorrectiveAction[] = [];

  get causeOptions(): SelectItem[] {
    const sortedCauses = [...this.potentialCauses].sort((a, b) => {
      if (a.percentage !== b.percentage) {
        return b.percentage - a.percentage;
      }
      return a.description.localeCompare(b.description);
    });

    return sortedCauses.map((cause) => ({
      label: `${cause.description} (${cause.percentage}%)`,
      value: cause.description,
    }));
  }

  get actionOptions(): SelectItem[] {
    const sortedActions = [...this.correctiveActions].sort((a, b) => {
      return a.description.localeCompare(b.description);
    });

    return sortedActions.map((action) => ({
      label: action.description,
      value: action.description,
    }));
  }

  showComments: boolean = false;
  showHistoryComments: boolean = false;
  showDetails = false;
  showCommentForm = false;
  newComment: string = '';
  messages$: Observable<Message[]> | undefined;
  activeTabIndex = 0;
  isEditMode = false;
  isLoadedEvaluationFinal: boolean = false;
  isForcedDisabled: boolean = false;
  private destroy$ = new Subject<void>();
  private roleService = inject(RoleService);

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private authService: AuthService,
    private evaluationService: EvaluationService,
    private alarmService: AlarmService,
    private confirmationService: ConfirmationService,
    private actions$: Actions,
  ) {
    this.evaluationForm = this.fb.group({
      severity: [null],
      potentialCause: [''],
      comments: [''],
      correctiveAction: [''],
      isFinal: [false],
      potentialConsequence: [''],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAlarm'] && this.selectedAlarm) {
      this.checkEditMode();
      this.loadPotentialCauses();
      this.loadCorrectiveActions();
      this.populateFormFromExistingEvaluation(this.selectedAlarm?.evaluation);
      this.updateFormValidators();
    }

    if (changes['evaluation']) {
      this.checkEditMode();
      const evaluationToUse = this.evaluation || this.selectedAlarm?.evaluation;
      this.populateFormFromExistingEvaluation(evaluationToUse);
      this.updateFormValidators();
    }
  }

  ngAfterContentInit(): void {
    this.actions$
      .pipe(
        ofType(discussionActions.loadDiscussionSuccess),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.scrollToBottom();
      });
  }

  toggleDetails(): void {
    this.showDetails = !this.showDetails;
  }

  toggleHistoryComments(): void {
    this.showHistoryComments = !this.showHistoryComments;
  }

  ngOnInit() {
    this.loadInitialData();
    this.subscribeToStore();
    this.messages$ = this.store.pipe(select(selectAllDiscussions));

    if (this.selectedAlarm) {
      this.checkEditMode();
      this.loadPotentialCauses();
      this.loadCorrectiveActions();
      this.populateFormFromExistingEvaluation(this.selectedAlarm?.evaluation);
      this.updateFormValidators();
    }

    this.evaluationForm
      .get('potentialCause')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {});
  }

  private updateFormValidators(): void {
    const severityControl = this.evaluationForm.get('severity');
    const commentsControl = this.evaluationForm.get('comments');

    severityControl?.clearValidators();
    commentsControl?.clearValidators();

    severityControl?.updateValueAndValidity();
    commentsControl?.updateValueAndValidity();
  }

  get currentStage(): Stage | undefined {
    return this.selectedAlarm?.rule?.stage;
  }

  get isDevOrQaStage(): boolean {
    return this.currentStage === Stage.Dev || this.currentStage === Stage.Qa;
  }

  get isProdManualStage(): boolean {
    return this.currentStage === Stage.ProdManual;
  }

  get isProdAutomatedStage(): boolean {
    return this.currentStage === Stage.ProdAutomated;
  }

  get shouldShowSeverityField(): boolean {
    return !this.isProdAutomatedStage;
  }

  get shouldShowPotentialCauseField(): boolean {
    return !this.isProdAutomatedStage;
  }

  get shouldShowCorrectiveActionField(): boolean {
    if (this.isProdAutomatedStage) {
      return true;
    }

    if (this.isDevOrQaStage) {
      return !!this.evaluationForm.get('potentialCause')?.value;
    }

    if (this.isProdManualStage) {
      return true;
    }

    return false;
  }

  get shouldShowCommentsField(): boolean {
    return !this.isProdAutomatedStage;
  }

  get shouldShowIsFinalCheckbox(): boolean {
    return !this.isProdAutomatedStage;
  }

  get isFormDisabled(): boolean {
    const currentEvaluation = this.evaluation || this.selectedAlarm?.evaluation;
    return (
      this.roleService.hasRole('SED_PLUS_SUPER_USER') === false ||
      this.isForcedDisabled ||
      (this.isEditMode && this.isLoadedEvaluationFinal) ||
      (this.isProdManualStage && currentEvaluation?.isVigieUpdated === true)
    );
  }

  get shouldShowSaveButton(): boolean {
    return (
      this.roleService.hasRole('SED_PLUS_SUPER_USER') &&
      (this.isDevOrQaStage ||
        this.isProdManualStage ||
        this.isProdAutomatedStage)
    );
  }

  get shouldShowSendToVigieButton(): boolean {
    const currentEvaluation = this.evaluation || this.selectedAlarm?.evaluation;
    return (
      this.roleService.hasRole('SED_PLUS_SUPER_USER') &&
      this.isProdManualStage &&
      currentEvaluation?.isSentToVigie === true &&
      currentEvaluation?.isVigieUpdated !== true
    );
  }

  get canSendToVigie(): boolean {
    if (!this.isProdManualStage) return false;

    const currentEvaluation = this.evaluation || this.selectedAlarm?.evaluation;
    if (currentEvaluation?.isVigieUpdated) return false;

    const form = this.evaluationForm;
    return !!(
      form.get('potentialCause')?.value && form.get('correctiveAction')?.value
    );
  }

  get shouldShowCloseEvaluationButton(): boolean {
    return this.roleService.hasRole('SED_PLUS_SUPER_USER');
  }

  get canCloseEvaluation(): boolean {
    const form = this.evaluationForm;

    if (this.isProdAutomatedStage) {
      return !!form.get('correctiveAction')?.value;
    }

    if (this.isDevOrQaStage) {
      return (
        form.get('potentialCause')?.value && form.get('correctiveAction')?.value
      );
    }

    if (this.isProdManualStage) {
      const currentEvaluation =
        this.evaluation || this.selectedAlarm?.evaluation;
      return (
        currentEvaluation?.isVigieUpdated === true &&
        form.get('potentialCause')?.value &&
        form.get('correctiveAction')?.value
      );
    }

    return false;
  }

  private loadInitialData(): void {}

  private loadPotentialCauses(): void {
    const ruleUid = this.selectedAlarm?.ruleFk;
    if (ruleUid) {
      this.store.dispatch(
        potentialCauseActions.loadPotentialCause({ ruleUid }),
      );
    }
  }

  private loadCorrectiveActions(): void {
    const ruleUid = this.selectedAlarm?.ruleFk;
    if (ruleUid) {
      this.store.dispatch(
        correctiveActionActions.loadCorrectiveAction({ ruleUid }),
      );
    }
  }

  private subscribeToStore(): void {
    combineLatest([
      this.store.pipe(select(selectPotentialCause)),
      this.store.pipe(select(selectCorrectiveAction)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([potentialCauses, correctiveActions]) => {
        this.potentialCauses = potentialCauses || [];
        this.correctiveActions = correctiveActions || [];
      });
  }

  private scrollToBottom(): void {
    this.commentElements?.last?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }

  backToDiscussions(): void {
    this.showComments = false;
    this.store.dispatch(discussionActions.resetDiscussion());
  }

  private checkEditMode(): void {
    const currentEvaluation = this.evaluation || this.selectedAlarm?.evaluation;
    this.isEditMode = !!currentEvaluation;
    this.isLoadedEvaluationFinal = currentEvaluation?.isFinal ?? false;
    this.updateFormState();
  }

  private updateFormState(): void {
    if (this.isEditMode && this.isLoadedEvaluationFinal) {
      this.evaluationForm.disable();
    } else {
      this.evaluationForm.enable();
    }
  }

  private populateFormFromExistingEvaluation(evaluation?: Evaluation): void {
    if (evaluation) {
      this.isLoadedEvaluationFinal = evaluation.isFinal ?? false;
      this.evaluationForm.patchValue({
        potentialCause: evaluation.potentialCause || '',
        comments: evaluation.comments,
        correctiveAction: evaluation.correctiveAction || '',
      });
    } else {
      this.isLoadedEvaluationFinal = false;
      this.evaluationForm.reset();
      this.evaluationForm.patchValue({
        isFinal: false,
        potentialConsequence: '',
      });
    }
    this.updateFormState();
  }

  onSubmit() {
    if (this.evaluationForm.invalid) {
      return;
    }

    const formValue = { ...this.evaluationForm.value };

    if (formValue.isFinal) {
      this.confirmationService.confirm({
        key: this.CONFIRM_DELETE,
        message:
          "Vous avez marqué l'évaluation comme finale, voulez-vous procéder?",
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Oui',
        rejectLabel: 'Non',
        accept: () => {
          this.submitEvaluation(formValue);
        },
        reject: () => {},
      });
    } else {
      this.submitEvaluation(formValue);
    }
  }

  onSendToVigie(): void {
    if (!this.canSendToVigie) {
      return;
    }

    this.confirmationService.confirm({
      key: this.CONFIRM_DELETE,
      message:
        'Êtes-vous sûr de vouloir mettre à jour Vigie avec cette évaluation?',
      header: 'Confirmation',
      icon: 'pi pi-send',
      acceptLabel: 'Oui',
      rejectLabel: 'Non',
      accept: () => {
        this.updateVigie();
      },
      reject: () => {},
    });
  }

  private updateVigie(): void {
    this.isUpdatingVigie = true;

    const formValue = this.evaluationForm.value;
    const currentEvaluation = this.evaluation || this.selectedAlarm?.evaluation;

    if (!currentEvaluation) {
      this.isUpdatingVigie = false;
      return;
    }

    this.store.dispatch(
      updateRuleEvaluationVigie({
        ruleUid: this.selectedAlarm!.ruleFk,
        evaluationUid: currentEvaluation.uid,
        potentialCause: formValue.potentialCause || '',
        correctiveAction: formValue.correctiveAction || '',
      }),
    );

    this.isUpdatingVigie = false;
  }

  onCloseEvaluation(): void {
    if (!this.canCloseEvaluation) {
      return;
    }

    const message = 'Êtes-vous sûr de vouloir fermer cette alerte?';

    this.confirmationService.confirm({
      key: this.CONFIRM_DELETE,
      message: message,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Non',
      accept: () => {
        this.closeEvaluation();
      },
      reject: () => {},
    });
  }

  private closeEvaluation(): void {
    this.isForcedDisabled = true;
    this.isLoadedEvaluationFinal = true;
    this.isEditMode = true;
    this.evaluationForm.disable();

    const formValue = this.evaluationForm.value;
    const currentEvaluation = this.evaluation || this.selectedAlarm?.evaluation;
    const alarmUid = this.selectedAlarm!.uid!;

    const evaluationObservable = currentEvaluation
      ? this.evaluationService.UpdateEvaluation({
          uid: currentEvaluation.uid,
          comments: formValue.comments || '',
          alarm_FK: alarmUid,
          isSentToVigie: currentEvaluation.isSentToVigie || false,
          isFinal: true,
          potentialCause: formValue.potentialCause || undefined,
          correctiveAction: formValue.correctiveAction || undefined,
          potentialConsequence: formValue.potentialConsequence || '',
        })
      : this.evaluationService.CreateEvaluation({
          comments: formValue.comments || '',
          alarm_FK: alarmUid,
          isFinal: true,
          isSentToVigie: formValue.isSentToVigie || false,
          potentialCause: formValue.potentialCause || undefined,
          correctiveAction: formValue.correctiveAction || undefined,
          potentialConsequence: formValue.potentialConsequence || '',
        });

    evaluationObservable.pipe(takeUntil(this.destroy$)).subscribe({
      next: (evaluation) => {
        this.alarmService
          .UpdateAlarmStatus({
            uid: alarmUid,
            status: AlarmStatus.Disabled,
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.store.dispatch(
                loadRule({ uniqueId: this.selectedAlarm!.ruleFk }),
              );
            },
            error: (error) => {
              console.error('Error updating alarm status:', error);
              this.isForcedDisabled = false;
              this.evaluationForm.enable();
            },
          });
      },
      error: (error) => {
        console.error('Error saving evaluation:', error);
        this.isForcedDisabled = false;
        this.evaluationForm.enable();
      },
    });
  }

  private submitEvaluation(formValue: any): void {
    if (this.isEditMode) {
      const currentEvaluation =
        this.evaluation || this.selectedAlarm?.evaluation;
      const updateEvaluationRequest: UpdateEvaluationRequest = {
        uid: currentEvaluation!.uid,
        comments: formValue.comments || '',
        alarm_FK: this.selectedAlarm!.uid!,
        isFinal: formValue.isFinal,
        isSentToVigie: currentEvaluation?.isSentToVigie ?? false,
        potentialCause: formValue.potentialCause || undefined,
        correctiveAction: formValue.correctiveAction || undefined,
        potentialConsequence: formValue.potentialConsequence || '',
      };

      this.store.dispatch(
        updateRuleEvaluation({
          ruleUid: this.selectedAlarm!.ruleFk,
          updateEvaluationRequest,
        }),
      );
    } else {
      const createEvaluationRequest: CreateEvaluationRequest = {
        comments: formValue.comments || '',
        alarm_FK: this.selectedAlarm!.uid || '',
        isSentToVigie: formValue.isSentToVigie || false,
        isFinal: formValue.isFinal,
        potentialCause: formValue.potentialCause || undefined,
        correctiveAction: formValue.correctiveAction || undefined,
        potentialConsequence: formValue.potentialConsequence || '',
      };

      this.store.dispatch(
        createRuleEvaluation({
          ruleUid: this.selectedAlarm!.ruleFk,
          createEvaluationRequest,
        }),
      );
    }
  }

  resetToCreateMode(): void {
    this.isEditMode = false;
    this.evaluationForm.reset();
    this.evaluationForm.patchValue({
      isFinal: false,
      potentialConsequence: '',
    });
    this.updateFormState();
  }

  addComment() {
    if (this.newComment.trim() === '') {
      console.error('Comment content is empty');
      return;
    }
    const userInfromation = this.authService.getUser();
    if (!userInfromation) {
      console.error('User information is not available');
      return;
    }
    const discussionMessage: AddCommentRequest = {
      sender: userInfromation?.email || '',
      content: this.newComment,
      discussion_fk: this.selectedAlarm?.evaluation?.discussion?.uid || '',
    };

    this.store.dispatch(
      discussionActions.addMessageToDiscussion({ discussionMessage }),
    );
    this.newComment = '';
  }

  getNameParts(email: string): string[] {
    const [localPart] = email.split('@');
    const parts = localPart.split('.');
    return parts.filter((part) => /^[A-Za-z]+$/.test(part));
  }

  extractInitials(email: string): string {
    const nameParts = this.getNameParts(email);
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  }

  extractFullName(email: string): string {
    const nameParts = this.getNameParts(email);
    const firstName = nameParts[0];
    const lastName =
      nameParts.length > 1 ? nameParts[nameParts.length - 1] : firstName;
    return `${firstName} ${lastName}`;
  }

  getPriorityLabel(priority: number): string | undefined {
    const match = this.priorityOptions.find((o) => o.value === priority);
    return match ? match.label : 'Inconnue';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
